import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";

import { menuService } from "../../services/menuService";
import { Product, Category } from "../../types/menu";
import PageTitle from "../../components/common/PageTitle";
import ProductsTable from "../../components/menu/ProductsTable";

const MenuProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );
  const [includeUnavailable, setIncludeUnavailable] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);

  // Fetch products and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch categories and products in parallel
        const [categoriesResponse, productsResponse] = await Promise.all([
          menuService.getCategories(),
          menuService.getProducts({
            search: searchQuery,
            category_id: selectedCategoryId ?? undefined,
            include_unavailable: includeUnavailable,
            skip: (page - 1) * pageSize,
            limit: pageSize,
          }) as any,
        ]);

        setCategories(categoriesResponse);

        // Handle response format - adjust based on your actual API response
        if (Array.isArray(productsResponse)) {
          setProducts(productsResponse);
          // If your API doesn't return a total count, we can estimate based on
          // if we received fewer items than the page size, we're on the last page
          setTotalProducts(
            productsResponse.length < pageSize && page > 1
              ? (page - 1) * pageSize + productsResponse.length
              : page * pageSize + 10, // Assume there are more items
          );
        } else if (productsResponse && typeof productsResponse === "object") {
          // If API returns { products: Product[], total: number }
          setProducts(productsResponse.products || []);
          setTotalProducts(productsResponse.total || 0);
        }
      } catch (err) {
        console.error("Failed to fetch menu data:", err);
        setError("Failed to load menu products. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    searchQuery,
    selectedCategoryId,
    includeUnavailable,
    refreshTrigger,
    page,
    pageSize,
  ]);

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1); // Reset to first page when searching
  };

  // Handle category filter change
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value ? parseInt(e.target.value) : null;

    setSelectedCategoryId(categoryId);
    setPage(1); // Reset to first page when changing category
  };

  // Handle toggle for including unavailable products
  const handleIncludeUnavailableChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIncludeUnavailable(e.target.checked);
    setPage(1); // Reset to first page when changing filter
  };

  // Handle product deletion
  const handleDeleteProduct = async (productId: number) => {
    try {
      setLoading(true);
      await menuService.deleteProduct(productId);
      setRefreshTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to delete product:", err);
      setError("Failed to delete product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle product availability toggle
  const handleToggleAvailability = async (product: Product) => {
    try {
      setLoading(true);
      await menuService.updateProductAvailability(product.id, {
        unavailable: !product.unavailable,
      });
      setRefreshTrigger((prev) => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalProducts / pageSize);

  return (
    <div className="space-y-6">
      <PageTitle
        actions={
          <Button
            color="primary"
            onPress={() => navigate("/menu/products/create")}
          >
            Add Product
          </Button>
        }
        subtitle="Manage your menu items and pricing"
        title="Menu Products"
      />

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <form onSubmit={handleSearch}>
              <Input
                className="w-full"
                placeholder="Search products"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
          </div>

          {/* Category filter */}
          <div>
            <label
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              htmlFor="category-filter"
            >
              Category
            </label>
            <select
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
              id="category-filter"
              value={selectedCategoryId?.toString() || ""}
              onChange={handleCategoryChange}
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Include unavailable checkbox */}
          <div className="flex items-end">
            <label className="flex items-center space-x-2">
              <input
                checked={includeUnavailable}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                type="checkbox"
                onChange={handleIncludeUnavailableChange}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Show unavailable products
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Products table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
        <ProductsTable
          categories={categories}
          loading={loading}
          products={products}
          onDelete={handleDeleteProduct}
          onEdit={(id) => navigate(`/menu/products/create?id=${id}`)}
          onToggleAvailability={handleToggleAvailability}
          onView={(id) => navigate(`/menu/products/${id}`)}
        />

        {/* Pagination controls */}
        <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between border-t border-gray-200 dark:border-gray-700 gap-4">
          <div className="flex items-center">
            <span className="text-sm text-gray-700 dark:text-gray-300 mr-2">
              Rows per page:
            </span>
            <select
              className="bg-transparent text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex items-center justify-center sm:justify-end space-x-2">
            <Button
              isDisabled={page === 1}
              size="sm"
              variant="light"
              onPress={() => handlePageChange(page - 1)}
            >
              Previous
            </Button>

            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Calculate which page numbers to show
                let pageNum;

                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                return (
                  <Button
                    key={pageNum}
                    color={pageNum === page ? "primary" : "default"}
                    size="sm"
                    variant={pageNum === page ? "solid" : "light"}
                    onPress={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>

            <Button
              isDisabled={page >= totalPages}
              size="sm"
              variant="light"
              onPress={() => handlePageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MenuProductsPage;
